import {
  AbstractComparatorApiService,
  AddressApiSuccessResponse,
  AddressData,
  basicHomeIProduct,
  CalculateParameters,
  Comparison,
  CustormerBenefitItem,
  houseNumberError,
  IComparatorApiServiceParameters,
  IProduct,
  QuoteResponse,
} from '.';
import { LegacyOrderResponse } from '../../helpers';
import type {
  FeaturesConnection,
  MasterAgreementStructure,
  OrderResult,
  OrderStatusResult,
} from './home';
import { Tripleplay } from './index';
import {
  getTypeFromQueryParams,
  replaceType,
  getEnergyTypeBaseOnUsage,
} from './product';
import { ApolloClient, createHttpLink, gql, InMemoryCache } from '@apollo/client';

function formatter(body: any, type: IProduct['type']): IProduct[] {
  if (body.data.length === 0) {
    return [
      {
        ...basicHomeIProduct,
        commonTechnical: [{
          sessionId: '',
          senderId: '',
          transactionReference: body.meta.requestLogId,
        }]
      }
    ]
  }
  return body.data.map(
    ({
      energy_label,
      features,
      filters,
      id,
      image,
      price,
      products,
      provider,
      usps,
      availability,
      review,
      is_auction_product,
    }) => {
      const { attributes, description, id: providerId, name } = provider;
      const {
        sustainabilityScore,
        sustainabilityInvestmentScore,
        sustainabilityCommerceScore,
        prosAndCons,
        annual_account,
        edit_amount,
        meter_readings,
        payment_method,
        postcoderoosregeling = '',
        usage,
        switchWindow,
      } = attributes;

      return {
        ...basicHomeIProduct,
        commonTechnical: [{
          ...basicHomeIProduct.commonTechnical,
          transactionReference: body.meta.requestLogId,
        }],
        bigProductFeature: {
          ...basicHomeIProduct['bigProductFeature'],
          hideIfZero: true,
          label: 'Besparing',
          value: {
            month: price.month.discountIncluded || 0,
            year: price.year.discountIncluded || 0,
          },
        },
        energy_label,
        is_auction_product,
        review,
        purchasableProduct: availability?.acceptance?.accepted,
        notPurchasebleReason: availability?.acceptance?.reason ?? '',
        availableContracts: availability?.available_contracts,
        features: features?.map((feature) => {
          return {
            id: feature.id,
            label: feature.name,
            value: feature.value,
          };
        }),
        filters,
        id,
        image,
        price: {
          breakdown: {
            ...basicHomeIProduct.price.breakdown,
            ...price.breakdown,
          },
          month: {
            ...basicHomeIProduct.price.month,
            ...price.month,
          },
          year: {
            ...basicHomeIProduct.price.year,
            ...price.year,
          },
        },
        priceInfo: {
          total: price.year.total,
          discountIncluded: price.year.discountIncluded,
        },
        prosAndCons: prosAndCons || basicHomeIProduct.prosAndCons,
        products,
        testScore: +sustainabilityScore,
        provider: {
          attributes: {
            annual_account: annual_account,
            edit_amount: edit_amount,
            meter_readings: meter_readings,
            payment_method: payment_method,
            postcoderoosregeling: postcoderoosregeling,
            usage: usage,
          },
          description,
          label: `Over ${name}`,
          id: providerId,
          name,
          postcoderoosregeling: postcoderoosregeling,
          sustainabilityScore: sustainabilityScore,
          sustainabilityInvestmentScore: sustainabilityInvestmentScore,
          sustainabilityCommerceScore: sustainabilityCommerceScore,
          switchWindowDays: switchWindow ? switchWindow.maxDays : 0,
        },
        usps: Object.keys(usps as object).length
          ? Object.entries(usps as object).map((usp) => {
              return {
                text: usp[1].content,
                type: usp[1].type,
              };
            })
          : [],
        type,
      };
    }
  );
}

export class LegacyComparisonApiService extends AbstractComparatorApiService {
  private client: any = {
    query: (queryData: any) => {
      const { path } = queryData;
      return fetch(path);
    },
  };

  constructor(params: IComparatorApiServiceParameters) {
    super(params);
    for (const attr in params) {
      this[attr] = params[attr];
    }
  }

  getClient(): any {
    return this.client;
  }

  async validateAddress(
    postcode: string,
    houseNumber: string,
    addition?: string
  ): Promise<AddressApiSuccessResponse> {
    let endpoint = `${this.baseUrl}/postcode/${postcode}/${houseNumber}`;
    if (addition) {
      endpoint += `/${addition}`;
    }
    const apiResponse = await fetch(endpoint, {
      headers: {
        Authorization: 'Basic ' + this.credentials.token,
      },
    });
    if (!apiResponse.ok || apiResponse.status !== 200) throw houseNumberError;
    let body: { data: Array<unknown> } = await apiResponse.json();
    if (
      body.data &&
      Array.isArray(body.data) &&
      body.data.length > 0 &&
      body.data[0].street &&
      body.data[0].city &&
      body.data[0].streetnumber &&
      body.data[0].nl_sixpp
    ) {
      const { street, streetnumber, nl_sixpp, city } = body.data[0];
      return {
        city: body.data[0].city,
        street: body.data[0].street,
        houseNumber: body.data[0].streetnumber,
        postcode: body.data[0].nl_sixpp,
        fullAddress: `${street} ${streetnumber} ${nl_sixpp}, ${city}`,
      };
    } else throw houseNumberError;
  }

  async calculateHomeContentPackage(
    calculateParameters: CalculateParameters
  ): Promise<MasterAgreementStructure> {
    return new Promise<MasterAgreementStructure>((resolve, reject) => {
      let calculateData: MasterAgreementStructure =
        {} as MasterAgreementStructure;
      resolve(calculateData);
      if (calculateParameters.houseNumber !== '26') {
        reject(calculateData);
      }
    });
  }

  async getAddressData(
    postalCode: string,
    houseNumber: string
  ): Promise<AddressData> {
    return new Promise<AddressData>((resolve) => {
      const addressData: AddressData = {
        postalCode: postalCode,
        houseNumber: parseInt(houseNumber),
      } as AddressData;

      resolve(addressData);
    });
  }

  async getHomeFeaturesDetails(
    productId: number | number[],
    after: string | null
  ): Promise<FeaturesConnection> {
    return new Promise<FeaturesConnection>((resolve) => {
      const data = {
        productId,
        after,
      } as any;

      resolve(data);
    });
  }

  async getComparison(id: string): Promise<Comparison> {
    const res = await fetch(this.baseUrl + '/comparisons/' + id, {
      headers: {
        Authorization: 'Basic ' + this.credentials.token,
      },
    });
    if (!res.ok || !res.json) throw new Error('500');
    const body = await res.json();
    if (!body.data || body.data.length < 1) throw new Error('400');
    const postcode = body.data[0].zipcode;
    const housenumber = body.data[0].streetNumber;
    let address;
    try {
      address = await this.validateAddress(postcode, housenumber);
    } catch (error) {
      console.warn('Address is invalid');
    }

    const data = body.data[0];
    const electricityUsageHigh = data.input.energy_usage_high;
    const electricityUsageLow = data.input.energy_usage_low;
    const gasusage = data.input.gas_usage;

    return {
      criteria: {
        housenumber,
        postcode,
        ...address,
        housenumberaddition: data.streetExtension,
        electricityusagehigh: electricityUsageHigh,
        electricityusagelow: electricityUsageLow,
        gasusage,
        currentprovider: data.input.previous_supplier_id,
        showPriceMode: 'month',
        filters: JSON.stringify({ showPriceMode: ['month'] }),
        defaultFilters: JSON.stringify({ showPriceMode: ['month'] }),

        energytype: getEnergyTypeBaseOnUsage(
          electricityUsageHigh,
          electricityUsageLow,
          gasusage
        ),
        ...body.data[0].input,
      },
    };
  }

  async getQuotePrefill(
    accessToken: string,
    swiftUserId: string,
    swiftProductId: string,
    productType: string
  ): Promise<QuoteResponse | false> {
    const res = await fetch(
      `${this.baseUrlTSN}/v1/user/${swiftUserId}/product/${swiftProductId}/quote-prefill/${productType}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    ).catch((error) => error);
    if (!res.ok || res.status !== 200) return false;
    const data = await res.json();
    let address;
    try {
      address = await this.validateAddress(
        data.postcode,
        String(data.housenumber)
      );
    } catch {
      console.warn('Address is invalid');
    }
    return { ...data, ...address };
  }

  async getProducts(
    searchCriteria: object,
    _selectedFilters?: object,
    requestOptions?: Parameters<typeof fetch>[1]
  ): Promise<IProduct[]> {
    const funnel = getTypeFromQueryParams(searchCriteria);
    let index = 0;
    let queryParams = '';
    for (const key in searchCriteria) {
      let separator = '&';
      if (index === 0) separator = '?';
      queryParams = queryParams + separator + key + '=' + searchCriteria[key];
      index++;
    }

    const res = await fetch(
      this.baseUrl + `/${replaceType(funnel, true)}/comparisons/` + queryParams,
      {
        headers: {
          Authorization: 'Basic ' + this.credentials.token,
          ...requestOptions?.headers,
        },
        ...requestOptions,
      }
    );
    console.log(res);
    if (!res.ok || res.status !== 200) return [];
    const body: { data: Array<unknown> } = await res.json();
    return formatter(body, funnel);
  }

  async calculateTriplePlay(): Promise<Tripleplay> {
    let calculateData: Tripleplay = {} as Tripleplay;
    return calculateData;
  }

  async getCustomerBenefits(): Promise<CustormerBenefitItem[]> {
    return Promise.resolve([]);
  }

  async getOrderDefinition(
    product: IProduct,
    productType: 'cars' | 'energy' | 'energyAuction'
  ): Promise<LegacyOrderResponse['data']> {
    const endpoint = `${this.baseUrl}/${
      productType === 'energyAuction' ? 'energy/auction' : productType
    }/products/${product.id}/order/?providerId=${product.provider.id}`;
    const res = await fetch(endpoint, {
      headers: {
        Authorization: 'Basic ' + this.credentials.token,
      },
    });
    if (res.ok) {
      const { data } = await res.json();
      return data;
    }
    throw res;
  }

  async orderAffiliateContentPackage(
    transactionReference: string,
    productCode: string
  ): Promise<OrderResult> {
    const data: OrderResult = {
      orderId: transactionReference,
      errors: productCode === 'error' ? [] : [productCode],
    };
    return Promise.resolve(data);
  }

  async sendEmailToCustomer(
    comparisonId: string | number,
    email: string
  ): Promise<string> {
    const body = new URLSearchParams();
    body.append('email', email);

    return new Promise((resolve) => {
      fetch(`${this.baseUrl}/comparisons/${comparisonId}/sendtoemail/`, {
        method: 'POST',
        headers: {
          Authorization: 'Basic ' + this.credentials.token,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
      }).then(() => {
        resolve('Email sent');
      });
    });
  }

  async getCalculationInput(
    transactionReference: string
  ): Promise<MasterAgreementStructure> {
    return new Promise<MasterAgreementStructure>((resolve, reject) => {
      let calculationInput: MasterAgreementStructure =
        {} as MasterAgreementStructure;
      if (transactionReference !== 'ae5a4233-1c18-496d-9684-8058d9cac391') {
        reject(calculationInput);
      }
      resolve(calculationInput);
    });
  }

  async orderStatus(orderId: string): Promise<OrderStatusResult> {
    const data: OrderStatusResult = {
      orderId: orderId,
      status: 'QUEUED_FOR_SAVING',
      underwritingResultStatus: 'ACCEPTED',
      errors: { message: '', code: 'OK' },
    };
    return Promise.resolve(data);
  }

  async checkMembershipStatus(email: string): Promise<any> {
    return Promise.resolve(email);
  }

  async addEnergyAuction(variables: any) {
    const ADD_ENERGY_AUCTION = gql`
        mutation AddEnergyAuction($AddEnergyAuctionInput: AddEnergyAuctionInput!)
        {
            addEnergyAuction(input:$AddEnergyAuctionInput)
            {
                energyAuctionAddedPayload {
                    id
                }
                errors {
                    ... on AlreadyExistsError {
                        message
                    }
                }
            }
        }
    `;
    const UPDATE_ENERGY_AUCTION = gql`
        mutation UpdateEnergyAuction($UpdateEnergyAuctionInput: UpdateEnergyAuctionInput!)
        {
            updateEnergyAuction(input:$UpdateEnergyAuctionInput)
            {
                energyAuctionAddedPayload {
                    id
                }
                errors {
                    ... on DoesNotExistError {
                        message
                    }
                }
            }
        }
    `;

    const publicDomain =
      this.baseUrl === 'https://api.overstappen.nl' ?
        'https://api.kizi.nl' :
        'https://api-acc-ext.kizi.nl';

    const apolloClient = new ApolloClient({
      link: createHttpLink({
        uri: `${publicDomain}/energy-auction/graphql`
      }),
      cache: new InMemoryCache()
    });

    const result = await apolloClient.mutate({
      mutation: ADD_ENERGY_AUCTION,
      variables: {
        AddEnergyAuctionInput: {
          'auction': {
            ...variables
          }
        }
      }
    });

    const errors = result.data.addEnergyAuction.errors;
    const hasErrors = errors.length > 0;

    if (hasErrors) {
      const recordExists = errors.find(e=>e.__typename === 'AlreadyExistsError') !== undefined;
      console.log({ recordExists });
      if (recordExists) {
        const updateResult = await apolloClient.mutate({
          mutation: UPDATE_ENERGY_AUCTION,
          variables: {
            UpdateEnergyAuctionInput: {
              'auction': {
                ...variables
              }
            }
          }
        });
        console.log(updateResult);
      }
    }
  }
}
